import { useTranslations } from '../../../../../../hooks/useTranslations';
import useOrderTracking from '../../../../../../hooks/useOrderTracking';
import { useGlobalSettings } from '../../../../../../hooks/useGlobalSettings';
import { renderTemplate } from '../../../../DeliveryStatus/utils';
import {
  usePageContext,
  useFormattedEdd,
} from '../../../../../../hooks/PageContext/usePageContext';

export interface EddHeaderContent {
  noEddTitle?: string;
  status?: string;
  edd?: { text: string; ariaLabel: string };
  orderNumber?: string;
}

const DEFAULT_EDD_ORDER = 'DAY_OF_WEEK,MONTH,DAY_OF_MONTH';

interface EddDateTokens {
  DAY_OF_WEEK: string;
  MONTH: string;
  DAY_OF_MONTH: string;
}

const isDateToken = (token: string): token is keyof EddDateTokens =>
  token === 'DAY_OF_WEEK' || token === 'MONTH' || token === 'DAY_OF_MONTH';

const formatEddDate = (tokens: EddDateTokens, tokenOrder: string) =>
  tokenOrder
    .split(',')
    .filter(isDateToken)
    .map((token) => `${tokens[token]}${token === 'DAY_OF_WEEK' ? ',' : ''}`)
    .join(' ');

export function formatEddLine(
  formattedEdds: string[][] | undefined,
  tokenOrder: string = DEFAULT_EDD_ORDER,
): EddHeaderContent['edd'] {
  const [begin, end] = formattedEdds ?? [];
  if (!begin) return undefined;

  if (end) {
    const [beginAria, beginDayOfWeek, , beginMonth, beginDayOfMonth] = begin;
    const [endAria, endDayOfWeek, , endMonth, endDayOfMonth] = end;
    if (
      !beginAria ||
      !beginDayOfWeek ||
      !beginMonth ||
      !beginDayOfMonth ||
      !endAria ||
      !endDayOfWeek ||
      !endMonth ||
      !endDayOfMonth
    ) {
      return undefined;
    }
    const beginText = formatEddDate(
      {
        DAY_OF_WEEK: beginDayOfWeek,
        MONTH: beginMonth,
        DAY_OF_MONTH: beginDayOfMonth,
      },
      tokenOrder,
    );
    const endText = formatEddDate(
      {
        DAY_OF_WEEK: endDayOfWeek,
        MONTH: endMonth,
        DAY_OF_MONTH: endDayOfMonth,
      },
      tokenOrder,
    );
    return {
      text: `${beginText} – ${endText}`,
      ariaLabel: `${beginAria} – ${endAria}`,
    };
  }

  const [ariaLabel, , dayOfWeek, month, dayOfMonth] = begin;
  if (!ariaLabel || !dayOfWeek || !month || !dayOfMonth) return undefined;
  return {
    text: formatEddDate(
      { DAY_OF_WEEK: dayOfWeek, MONTH: month, DAY_OF_MONTH: dayOfMonth },
      tokenOrder,
    ),
    ariaLabel,
  };
}

const DELIVERED_STATUSES = ['DELIVERED', 'LDDELIVERED', 'MVDELIVERED'];

export function useEddHeader(): EddHeaderContent | null {
  const {
    trackingStatus,
    trackingStatusNoEddTitle,
    trackingStatusScheduleDelivery,
    trackingStatusDeliveryScheduled,
    blockEddDateTokenOrder,
    retailMediaEddDrawerArrivingText,
  } = useTranslations();
  const { internationalDateFormatting } = useGlobalSettings();
  const { getCurrentShipment } = useOrderTracking();
  const formattedEdd = useFormattedEdd();
  const {
    state: { orderNumbers },
  } = usePageContext();

  const shipment = getCurrentShipment();
  if (!shipment) return null;

  const { displayDate, scheduling, status: shipmentStatus } = shipment;
  const schedulable = !!scheduling?.isSchedulable && !scheduling.isScheduled;
  const noEddTitle =
    (!schedulable && !scheduling?.isScheduled && !displayDate
      ? trackingStatusNoEddTitle
      : undefined) || undefined;

  const status =
    (schedulable
      ? trackingStatusScheduleDelivery
      : scheduling?.isScheduled
        ? trackingStatusDeliveryScheduled
        : trackingStatus) || undefined;
  const tokenOrder =
    internationalDateFormatting && typeof blockEddDateTokenOrder === 'string'
      ? blockEddDateTokenOrder || DEFAULT_EDD_ORDER
      : DEFAULT_EDD_ORDER;
  const eddLine = schedulable
    ? undefined
    : formatEddLine(formattedEdd?.formattedEdds, tokenOrder);
  const arrivingTemplate =
    shipmentStatus && DELIVERED_STATUSES.includes(shipmentStatus)
      ? null
      : retailMediaEddDrawerArrivingText;
  const edd =
    eddLine && arrivingTemplate
      ? {
          text: renderTemplate(arrivingTemplate, { date: eddLine.text }),
          ariaLabel: renderTemplate(arrivingTemplate, {
            date: eddLine.ariaLabel,
          }),
        }
      : eddLine;

  if (!noEddTitle && !status && !edd) return null;

  return { noEddTitle, status, edd, orderNumber: orderNumbers?.[0] };
}
